import React, { FC, useMemo, useState } from "react";

import {
	Badge,
	Button,
	ConfirmModal,
	Dropdown,
	EditableCard,
	Grid,
	Link,
	Tabs,
	TMenuItem,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";

import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useMonthPeriods } from "@/hooks/useMonthPeriods";

import { MembersCard } from "@/components/organisms/MembersCard/MembersCard";
import { ProjectFormModal } from "@/components/organisms/ProjectFormModal/ProjectFormModal";
import { ProjectsCard } from "@/components/organisms/ProjectsCard/ProjectsCard";
import { PageLayout } from "@/components/templates";

import { meKeys, companyKeys } from "@/constants/CQueryKeys";

import type { TSection } from "@/types";
import { TCompany } from "@/types";
import { TCompanyKind } from "@/types";

import { promiseToast } from "@/modules/index";

import { COMPANY_DETAILS_SECTIONS } from "./CCompanyDetailsSection";
import { CompanyBanner } from "./components/CompanyBanner";
import { CompanyBillingTab } from "./components/CompanyBillingTab";

type TCompanyTab = "identity" | "billing";

/** The API's `Assert\Url` rejects a bare domain: "acme.fr" → "https://acme.fr". */
export const normalizeWebsite = (value: string): string => {
	const trimmed = value.trim();
	if (!trimmed || /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
};

/* eslint-disable react/display-name -- not a component (a `valueDisplay`
   callback, called directly rather than rendered as JSX) — this heuristic
   still fires on any function returning JSX regardless. */
const linkValueDisplay =
	(hrefPrefix: string | true, field: keyof TCompany) => (values: object) => {
		const value = (values as Record<string, unknown>)[field] as
			string | undefined;
		if (!value) return "-";
		const href = hrefPrefix === true ? value : `${hrefPrefix}${value}`;
		return <Link href={href}>{value}</Link>;
	};

/* eslint-enable react/display-name */

const fieldNamesOf = (sections: TSection[]): string[] =>
	sections.flatMap((section) => section.fields.map((field) => field.name));

interface ICompanyDetailViewProps {
	kind: TCompanyKind;
}

/**
 * One of the user's companies or clients - one view, see `kinds.ts`.
 * Editable by its owner only — anyone else with access (a report's
 * reader...) gets the read-only cards.
 */
export const CompanyDetailView: FC<ICompanyDetailViewProps> = ({ kind }) => {
	const { id: idParam } = useParams<{ id: string }>();
	const id = Number(idParam);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
	const [isCreatingProject, setIsCreatingProject] = useState(false);
	const [tab, setTab] = useState<TCompanyTab>("identity");
	const { current } = useMonthPeriods();

	// Built inside the component, not module-scope: `t()` is i18next's
	// plain export, so a module-level constant would resolve every label
	// at import time — before i18next.init() — to its raw key.
	const sections = useMemo(
		() =>
			COMPANY_DETAILS_SECTIONS({
				normalizeWebsite,
				linkValueDisplay,
				withDefaultRate: kind.collection === "clients",
			}),
		[kind.collection]
	);
	const fieldNames = fieldNamesOf(sections);

	const {
		isLoading,
		error,
		data: company,
	} = useQuery({
		queryKey: companyKeys.detail(kind.collection, id),
		queryFn: () => CompaniesService.fetchCompany(id),
		enabled: !Number.isNaN(id),
	});

	const canEdit = !!company && !!user && company.ownerId === user.id;
	// Paused features (feature_flags): the billing settings serve invoices
	// only (the default rate sits with the identity), members serve sharing.
	const invoicesOn = useFeatureFlag("invoices");
	const hasBillingTab = kind.hasBillingTab && invoicesOn;
	const sharingOn = useFeatureFlag("sharing");

	// `me` carries the user's own company; a client isn't on it.
	const invalidate = () =>
		Promise.all([
			queryClient.invalidateQueries({
				queryKey: companyKeys.collection(kind.collection),
			}),
			queryClient.invalidateQueries({
				queryKey: companyKeys.detail(kind.collection, id),
			}),
			...(kind.collection === "companies"
				? [queryClient.invalidateQueries({ queryKey: meKeys.all })]
				: []),
		]);

	const handleCompanyLogoChange = (file: File) => {
		const prom = CompaniesService.updateLogo(file, id).then((res) => {
			queryClient.setQueryData(
				[kind.collection, id],
				(oldData: TCompany | undefined) =>
					oldData && { ...oldData, logo: res.url }
			);
			// The list shows the logo too; `exact` keeps the detail entry
			// just set from being refetched right away.
			queryClient.invalidateQueries({
				queryKey: companyKeys.collection(kind.collection),
				exact: true,
			});
			return res;
		});
		promiseToast(prom);
		return prom;
	};

	// One card, one request: the payload is that card's fields (a cleared
	// one stays `""`, the API turns it into null) plus the id — not the
	// whole company record the card hands back. The API leaves out every
	// field the payload omits, `name` included.
	const handleSubmit = (section: TSection) => (values: object) => {
		const record = values as Record<string, unknown>;
		const payload = Object.fromEntries(
			section.fields
				.map((field) => field.name)
				.filter((name) => name in record)
				.map((name) => [name, record[name]])
		);
		if (typeof payload.website === "string") {
			payload.website = normalizeWebsite(payload.website);
		}
		// A cleared number field arrives as null; the API clears on "".
		for (const name of numberFields) {
			if (name in payload && payload[name] === null) payload[name] = "";
		}
		const prom = CompaniesService.update({ ...payload, id }).then(
			invalidate
		);
		promiseToast(prom);
		return prom.then(() => {});
	};

	const deleteMutation = useMutation({
		mutationFn: () => CompaniesService.delete(id),
		onSuccess: async () => {
			await invalidate();
			navigate(kind.basePath);
		},
	});

	// The card wants strings (a nullable field comes back as null from
	// the API) - except a number field, which takes null for empty.
	const numberFields = new Set(
		sections
			.flatMap((section) => section.fields)
			.filter((field) => field.type === "number")
			.map((field) => field.name)
	);
	const formValues =
		company &&
		Object.fromEntries(
			fieldNames.map((name) => [
				name,
				(company as Record<string, unknown>)[name] ??
					(numberFields.has(name) ? null : ""),
			])
		);

	// Rare, destructive: behind a "more" menu rather than a top-level
	// button, so the page's most visible control isn't "Delete".
	const menuItems: TMenuItem[] = [
		{
			label: t("actions.delete"),
			picto: "trash",
			color: "danger",
			onClick: () => setIsConfirmingDelete(true),
		},
	];

	return (
		<>
			<PageLayout
				title={company?.name ?? t("company.label")}
				titleAfter={
					canEdit && kind.showOwnerBadge ? (
						<Badge color="primary" size="sm" pill>
							{t("company.owner")}
						</Badge>
					) : undefined
				}
				isLoading={isLoading}
				error={
					error ? true : !company ? t(`${kind.i18n}.notFound`) : false
				}
				onBack={() => navigate(kind.basePath)}
				onBackLabel={t(`${kind.i18n}.back`)}
				actions={
					canEdit
						? [
								<Dropdown
									key="more"
									items={menuItems}
									placement="bottom-end"
								>
									<Button
										variant="outline"
										picto="more"
										aria-label={t("actions.more")}
									/>
								</Dropdown>,
							]
						: undefined
				}
			>
				<div className="flex flex-col gap-8">
					{!!company && (
						<CompanyBanner
							company={company}
							canEdit={canEdit}
							onLogoChange={handleCompanyLogoChange}
						/>
					)}
					{!!company && hasBillingTab && (
						<Tabs<TCompanyTab>
							items={[
								{
									value: "identity",
									label: t("company.billing.identityTab"),
								},
								{
									value: "billing",
									label: t("company.billing.tab"),
								},
							]}
							value={tab}
							onChange={setTab}
						/>
					)}
					{/* Mounted on demand: the billing settings are fetched
					    when the tab opens, not with the identity. */}
					{!!company && hasBillingTab && tab === "billing" && (
						<CompanyBillingTab companyId={id} canEdit={canEdit} />
					)}
					{!!company && (tab === "identity" || !hasBillingTab) && (
						<Grid columns={2} minItemWidth="400px">
							{sections.map((section) => (
								<EditableCard
									key={section.key}
									title={section.title}
									className="w-full"
									fields={section.fields}
									values={formValues}
									validationSchema={section.validationSchema}
									onSubmit={handleSubmit(section)}
									editInModal
									formikWrapper
									readOnly={!canEdit}
									editColumns={2}
									modalProps={{ size: "lg" }}
									// The app's own wording: the lib's
									// defaults follow AmphoreProvider's
									// locale, not i18next's.
									editLabel={t("actions.edit")}
									submitLabel={t("actions.save")}
									cancelLabel={t("actions.cancel")}
								/>
							))}
							{/* Only a client is worked on: an issuer of one's own
					    is an identity on a report, not a room to open. */}
							{!!company && kind.collection === "clients" && (
								<ProjectsCard
									scope="clients"
									id={id}
									period={current}
									canCreate={canEdit}
									onCreate={() => setIsCreatingProject(true)}
								/>
							)}
							{!!company &&
								kind.collection === "clients" &&
								sharingOn && (
									<MembersCard
										scope="clients"
										id={id}
										canManage={canEdit}
										currentUserId={user?.id ?? 0}
									/>
								)}
						</Grid>
					)}
				</div>
			</PageLayout>
			<ConfirmModal
				open={isConfirmingDelete}
				onClose={() => setIsConfirmingDelete(false)}
				size="sm"
				title={t(`${kind.i18n}.delete.title`)}
				onConfirm={() => {
					promiseToast(deleteMutation.mutateAsync());
					setIsConfirmingDelete(false);
				}}
				confirmText={t("actions.delete")}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
			>
				{t(`${kind.i18n}.delete.message`)}
			</ConfirmModal>
			{/* Opened from the card above, so the client is already chosen. */}
			{!!company && kind.collection === "clients" && (
				<ProjectFormModal
					open={isCreatingProject}
					client={company}
					onClose={() => setIsCreatingProject(false)}
					onSaved={(project) => navigate(`/projects/${project.id}`)}
				/>
			)}
		</>
	);
};
